import React, { useState, useEffect } from 'react';
import { useLocation, useNavigate } from 'react-router-dom';
import styled from 'styled-components';
import projectService from '../services/projectService';
import { useAuth } from '../contexts/AuthContext';
import engagementService from '../services/engagementService';
import { motion, AnimatePresence } from 'framer-motion';
import Icon from './Icon';
// Tooltip removed per latest requirements

const LayoutContainer = styled.div`
  min-height: 100vh;
  display: flex;
  background: var(--gradient-bg);
`;

const Sidebar = styled(motion.aside)`
  width: ${props => props.$isCollapsed ? '80px' : '280px'};
  background: var(--gradient-card);
  border-right: 1px solid var(--border-color);
  backdrop-filter: blur(10px);
  transition: width 0.3s ease;
  position: fixed;
  height: 100vh;
  z-index: 100;
  overflow: hidden;
  display: flex;
  flex-direction: column;

  @media (max-width: 768px) {
    width: ${props => props.$isOpen ? '280px' : '-280px'};
    transform: translateX(${props => props.$isOpen ? '0' : '-100%'});
  }
`;

const SidebarContent = styled.div`
  display: flex;
  flex-direction: column;
  height: 100%;
`;

const MainContent = styled.main`
  flex: 1;
  margin-left: ${props => props.$sidebarCollapsed ? '80px' : '280px'};
  transition: margin-left 0.3s ease;

  @media (max-width: 768px) {
    margin-left: 0;
  }
`;


const SidebarHeader = styled.div`
  padding: 1.1rem 1.25rem 0.9rem;
  border-bottom: 1px solid var(--border-color);
  text-align: center;
  position: relative;
  flex: 0 0 auto;
`;

const LogoContainer = styled.div`
  display: flex;
  align-items: center;
  justify-content: center;
  transition: all 0.3s ease;
`;

const LogoImage = styled.img`
  height: ${props => props.$collapsed ? '30px' : '46px'};
  width: ${props => props.$collapsed ? '38px' : '104px'};
  object-fit: contain;
  transition: all 0.3s ease;
`;

const Switcher = styled.div`
  margin: 0.75rem 0.75rem 0.25rem;
  position: relative;
  flex: 0 0 auto;
`;

const SwitcherButton = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.55rem;
  background: var(--surface-3);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  padding: 0.55rem 0.65rem;
  color: var(--text-primary);
  font-family: var(--font-primary);
  font-size: 0.88rem;
  cursor: pointer;
  text-align: left;

  &:hover { border-color: var(--border-strong); }
`;

const Dot = styled.span`
  width: 7px;
  height: 7px;
  border-radius: 50%;
  background: #34d399;
  flex: 0 0 auto;
`;

const SwitcherName = styled.span`
  flex: 1;
  min-width: 0;
  overflow: hidden;
  text-overflow: ellipsis;
  white-space: nowrap;
`;

const Chevron = styled.span`
  color: var(--text-muted);
  display: flex;
`;

const Dropdown = styled.div`
  position: absolute;
  left: 0;
  right: 0;
  top: calc(100% + 0.3rem);
  background: var(--primary-bg);
  border: 1px solid var(--border-color);
  border-radius: var(--radius-md);
  overflow: hidden;
  z-index: 30;
  box-shadow: 0 12px 32px rgba(0, 0, 0, 0.45);
`;

const DropRow = styled.button`
  width: 100%;
  display: flex;
  align-items: center;
  gap: 0.5rem;
  background: ${(p) => (p.$on ? 'rgba(255,255,255,0.04)' : 'none')};
  border: none;
  border-top: ${(p) => (p.$divider ? '1px solid var(--border-color)' : 'none')};
  padding: 0.5rem 0.7rem;
  color: ${(p) => (p.$accent ? 'var(--accent-cyan)' : 'var(--text-secondary)')};
  font-family: var(--font-primary);
  font-size: 0.85rem;
  cursor: pointer;
  text-align: left;

  &:hover { color: var(--text-primary); background: rgba(255, 255, 255, 0.05); }
`;

const DropRole = styled.span`
  margin-left: auto;
  font-size: 0.7rem;
  color: var(--text-muted);
`;

const NavHeading = styled.div`
  font-size: 0.62rem;
  letter-spacing: 0.14em;
  text-transform: uppercase;
  color: var(--text-muted);
  padding: 0.9rem 1.5rem 0.35rem;
`;

/*
 * The scrolling region.
 *
 * This is where the sidebar was broken: it sits inside a fixed 100vh column
 * with `overflow: hidden`, and had neither `flex: 1` nor an overflow rule of
 * its own. Anything past the fold was simply clipped with no way to reach it -
 * on a 768px laptop that started around Integrations.
 *
 * `min-height: 0` is the part that is easy to miss: a flex child defaults to
 * `min-height: auto`, which refuses to shrink below its content, so the
 * overflow rule never engages without it.
 */
const NavMenu = styled.nav`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  overscroll-behavior: contain;
  padding: 0.5rem 0 1rem;
`;

// Draws attention to work waiting on a human, so the queue is not missed.
const NavBadge = styled.span`
  margin-left: auto;
  min-width: 1.4rem;
  padding: 0.1rem 0.4rem;
  border-radius: 999px;
  background: var(--accent-cyan);
  color: var(--primary-bg);
  font-size: 0.72rem;
  font-weight: 700;
  text-align: center;
  line-height: 1.4;
`;

/*
 * A navigation row.
 *
 * The selected row used to be a full gradient pill with a glow, and hovering
 * slid every row 5px to the right. That is a lot of noise for the one control
 * you look at most and click least deliberately. Selection is now a quiet
 * raised surface with a cyan rail on the leading edge - unmistakable, and
 * quiet enough to sit next to eight of its neighbours.
 */
const NavItem = styled(motion.div)`
  position: relative;
  width: calc(100% - 1rem);
  margin: 0.1rem 0.5rem;
  padding: 0.6rem 0.85rem;
  cursor: pointer;
  display: flex;
  align-items: center;
  gap: 0.8rem;
  color: var(--text-secondary);
  font-size: 0.9rem;
  border-radius: var(--radius-md);
  transition: background var(--transition-fast), color var(--transition-fast);

  &:hover {
    background: var(--surface-hover);
    color: var(--text-primary);
  }

  &.active {
    background: var(--accent-cyan-dim);
    color: var(--accent-cyan);
    font-weight: 600;
  }

  &.active::before {
    content: '';
    position: absolute;
    left: -0.5rem;
    top: 50%;
    transform: translateY(-50%);
    width: 3px;
    height: 1.15rem;
    border-radius: 0 3px 3px 0;
    background: var(--accent-cyan);
  }

  .text {
    opacity: ${props => props.$collapsed ? 0 : 1};
    transition: opacity 0.2s ease;
    white-space: nowrap;
    overflow: hidden;
  }
`;

const UserProfile = styled.div`
  flex: 0 0 auto;
  padding: 0.9rem 1.1rem;
  border-top: 1px solid var(--border-color);
  margin-top: auto;
  display: flex;
  flex-direction: ${props => props.$collapsed ? 'column' : 'row'};
  align-items: center;
  gap: ${props => props.$collapsed ? '0.5rem' : '1rem'};
  background: rgba(10, 12, 22, 0.5);
`;


const UserInfo = styled.div`
  flex: 1;
  /* Without this a long address refuses to shrink and shoves the sign-out
     button until its own label wraps onto two lines. */
  min-width: 0;
  opacity: ${props => props.$collapsed ? 0 : 1};
  transition: opacity 0.3s ease;

  .name, .email {
    overflow: hidden;
    text-overflow: ellipsis;
    white-space: nowrap;
  }

  .name {
    font-weight: 600;
    color: var(--text-primary);
    font-size: var(--text-sm);
  }

  .email {
    color: var(--text-muted);
    font-size: var(--text-xs);
  }
`;

const LogoutButton = styled.button`
  border: 1px solid var(--border-color);
  background: transparent;
  color: var(--text-secondary);
  padding: ${props => props.$collapsed ? '0.4rem' : '0.45rem 0.9rem'};
  border-radius: var(--radius-md);
  font-size: ${props => props.$collapsed ? '1rem' : '0.8rem'};
  font-weight: 600;
  cursor: pointer;
  transition: all 0.3s ease;
  min-width: ${props => props.$collapsed ? '32px' : 'auto'};
  height: ${props => props.$collapsed ? '32px' : 'auto'};
  display: flex;
  align-items: center;
  justify-content: center;
  flex: 0 0 auto;
  white-space: nowrap;

  &:hover {
    color: var(--text-primary);
    border-color: var(--border-strong);
    background: var(--surface-hover);
  }
`;


const CollapseToggle = styled.button`
  background: none;
  border: none;
  color: var(--accent-cyan);
  font-size: 1.25rem;
  cursor: pointer;
  padding: 0.5rem;
  border-radius: var(--radius-md);
  transition: all 0.3s ease;
  position: absolute;
  top: 1rem;
  right: 1rem;
  z-index: 10;

  &:hover {
    background: rgba(34, 211, 238, 0.1);
    transform: scale(1.1);
  }

  @media (max-width: 768px) {
    display: none;
  }
`;

const Overlay = styled(motion.div)`
  position: fixed;
  top: 0;
  left: 0;
  right: 0;
  bottom: 0;
  background: rgba(0, 0, 0, 0.5);
  z-index: 99;
  display: none;

  @media (max-width: 768px) {
    display: block;
  }
`;

const ContentArea = styled.div`
  padding: 2rem;
  min-height: calc(100vh - 80px);
`;

const Layout = ({ children }) => {
  const { user, logout } = useAuth();
  const location = useLocation();
  const navigate = useNavigate();
  const [sidebarCollapsed, setSidebarCollapsed] = useState(false);
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [pendingApprovals, setPendingApprovals] = useState(0);

  useEffect(() => {
    let cancelled = false;
    const loadPending = async () => {
      try {
        const stats = await engagementService.getStats();
        if (!cancelled) setPendingApprovals(stats.pending || 0);
      } catch {
        // A badge is decorative; never let it break navigation.
      }
    };
    loadPending();
    const id = setInterval(loadPending, 60000);
    return () => { cancelled = true; clearInterval(id); };
  }, []);

  // Which project you are in, and the others you could switch to.
  const [projects, setProjects] = useState([]);
  const [currentId, setCurrentId] = useState(projectService.current());
  const [switcherOpen, setSwitcherOpen] = useState(false);

  useEffect(() => {
    let cancelled = false;
    projectService.list()
      .then((list) => {
        if (cancelled) return;
        setProjects(list);
        // Nothing chosen yet, or a stale choice: fall back to the first one, so
        // the switcher never shows a project that is no longer there.
        const known = list.some((p) => p.id === projectService.current());
        if (!known) {
          // Clearing comes first and happens even when the list is empty. A
          // brand new account has no projects, so `list.length` was false and
          // the stale id survived exactly where it does the most damage: every
          // request carried a project this person is not in, and the server
          // refused all of them.
          projectService.clearCurrent();
          if (list.length) {
            projectService.setCurrent(list[0].id);
            setCurrentId(list[0].id);
          } else {
            setCurrentId(null);
          }
        }
      })
      .catch(() => setProjects([]));
    return () => { cancelled = true; };
  }, []);

  const currentProject = projects.find((p) => p.id === currentId) || projects[0] || null;

  const switchTo = (id) => {
    projectService.setCurrent(id);
    setCurrentId(id);
    setSwitcherOpen(false);
    // A hard reload rather than a re-render: every page loads its own data on
    // mount, and half of them would otherwise still be showing the old project.
    window.location.reload();
  };

  // Grouped rather than flat: what you do day to day, then the project itself,
  // then the account. "Agent" stops being a destination - it was never a place
  // you go, it is the thing everything else belongs to.
  const navigationItems = [
    { icon: 'dashboard', text: 'Dashboard', path: '/dashboard' },
    { icon: 'workflow', text: 'Workflows', path: '/workflow' },
    { icon: 'leads', text: 'Leads', path: '/leads' },
    { icon: 'approvals', text: 'Approvals', path: '/approvals' },
    { icon: 'conversations', text: 'Conversations', path: '/conversations' },
    { heading: 'Project' },
    { icon: 'settings', text: 'Settings', path: '/settings' },
    { icon: 'people', text: 'People', path: '/people' },
    { heading: 'Account' },
    { icon: 'integrations', text: 'Integrations', path: '/integrations' },
  ];

  const handleLogout = () => {
    logout();
  };

  const toggleSidebar = () => {
    setSidebarCollapsed(!sidebarCollapsed);
  };

  const toggleMobileMenu = () => {
    setMobileMenuOpen(!mobileMenuOpen);
  };

  const handleNavigation = (path) => {
    navigate(path);
    if (mobileMenuOpen) {
      setMobileMenuOpen(false);
    }
  };


  return (
    <LayoutContainer>
      <AnimatePresence>
        {mobileMenuOpen && (
          <Overlay
            initial={{ opacity: 0 }}
            animate={{ opacity: 1 }}
            exit={{ opacity: 0 }}
            onClick={toggleMobileMenu}
          />
        )}
      </AnimatePresence>

      <Sidebar
        $isCollapsed={sidebarCollapsed}
        $isOpen={mobileMenuOpen}
        initial={false}
        animate={{ x: 0 }}
        transition={{ type: "spring", stiffness: 300, damping: 30 }}
      >
        <SidebarContent>
          <CollapseToggle onClick={toggleSidebar}>
            <Icon name={sidebarCollapsed ? 'chevronRight' : 'chevronLeft'} size={14} />
          </CollapseToggle>

          <SidebarHeader>
            <LogoContainer>
              <LogoImage
                src="/orion-logo-light.png"
                alt="ORION AI"
                $collapsed={sidebarCollapsed}
              />
            </LogoContainer>
          </SidebarHeader>

          {!sidebarCollapsed && currentProject && (
            <Switcher>
              <SwitcherButton onClick={() => setSwitcherOpen(!switcherOpen)}>
                <Dot />
                <SwitcherName>{currentProject.name}</SwitcherName>
                <Chevron><Icon name="chevronDown" size={14} /></Chevron>
              </SwitcherButton>

              {switcherOpen && (
                <Dropdown>
                  {projects.map((p) => (
                    <DropRow key={p.id} $on={p.id === currentProject.id} onClick={() => switchTo(p.id)}>
                      {p.name}
                      <DropRole>{p.role}</DropRole>
                    </DropRow>
                  ))}
                  <DropRow $divider $accent onClick={() => { setSwitcherOpen(false); handleNavigation('/projects/new'); }}>
                    <Icon name="plus" size={14} /> New project
                  </DropRow>
                  <DropRow $accent onClick={() => { setSwitcherOpen(false); handleNavigation('/projects/join'); }}>
                    Join with a code
                  </DropRow>
                </Dropdown>
              )}
            </Switcher>
          )}

          <NavMenu>
            {navigationItems.map((item, index) => (
              item.heading ? (
                !sidebarCollapsed && <NavHeading key={index}>{item.heading}</NavHeading>
              ) : (
                <NavItem
                  key={index}
                  $collapsed={sidebarCollapsed}
                  className={location.pathname === item.path ? 'active' : ''}
                  onClick={() => handleNavigation(item.path)}
                >
                  <Icon name={item.icon} size={18} />
                  <span className="text">{item.text}</span>
                  {item.path === '/approvals' && pendingApprovals > 0 && !sidebarCollapsed && (
                    <NavBadge>{pendingApprovals}</NavBadge>
                  )}
                </NavItem>
              )
            ))}
          </NavMenu>

          <UserProfile $collapsed={sidebarCollapsed}>
            <UserInfo $collapsed={sidebarCollapsed}>
              <div className="name">{user?.fullName || 'User'}</div>
              <div className="email">{user?.email}</div>
            </UserInfo>
            <LogoutButton onClick={handleLogout} $collapsed={sidebarCollapsed}>
              {sidebarCollapsed ? <Icon name="power" size={17} /> : 'Sign out'}
            </LogoutButton>
          </UserProfile>
        </SidebarContent>
      </Sidebar>

      <MainContent $sidebarCollapsed={sidebarCollapsed}>


        <ContentArea>
          {children}
        </ContentArea>
      </MainContent>
    </LayoutContainer>
  );
};

export default Layout;
